import { Injectable } from '@angular/core';
import { WaseelWidgetConfigService } from './waseel-widget-config.service';

@Injectable({
  providedIn: 'root'
})
export class WaseelWidgetService {
  private scriptLoaded = false;

  constructor(private configService: WaseelWidgetConfigService) {}

  loadWidget(environment: 'production' | 'staging' = 'production', delay = 0): void {
    if (this.scriptLoaded) {
      console.log('Waseel widget already loaded');
      return;
    }
    
    this.scriptLoaded = true;

    // Preload widget assets to prevent 404 errors
    this.configService.preloadWidgetAssets();

    // Get configuration and apply it early
    const config = this.configService.getConfig(environment);
    this.configService.applyConfig({
      ...config,
      customCSS: this.configService.getDefaultCustomCSS()
    });

    // Original widget URLs
    const WIDGET_URL =
      environment === 'production'
        ? 'https://waseel.agent.labeah.ai/widget.js'
        : 'https://waseel.agent.flitc.tech/widget.js';

    const load = () => {
      // Check if script already exists to prevent duplicates
      const existingScript = document.querySelector(`script[src="${WIDGET_URL}"]`);
      if (existingScript) {
        console.log('Waseel widget script already exists');
        return;
      }

      const script = document.createElement('script');
      script.src = WIDGET_URL;
      script.async = true;
      script.id = 'waseel-widget-script';

      script.onload = () => {
        console.log(`Waseel widget loaded successfully (${environment})`);
        // Add a small delay to ensure the widget is fully initialized
        setTimeout(() => {
          this.initializeWidget();
        }, 1500);
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Waseel widget:', error);
        this.scriptLoaded = false; // Reset to allow retry
      };

      document.head.appendChild(script);
    };

    // Add delay if specified
    if (delay > 0) {
      setTimeout(() => {
        load();
      }, delay);
    } else {
      load();
    }
  }

  initializeWidget(): void {
    if (typeof window === 'undefined') {
      console.warn('Window object not available, cannot initialize Waseel widget');
      return;
    }

    try {
      // Check if Waseel widget object exists on window in various forms
      const windowAny = window as any;
      
      // Try different possible widget object names
      const possibleWidgetObjects = [
        'WaseelWidget',
        'waseel',
        'Waseel', 
        'waseelWidget',
        'WaseelChat',
        'waseelChat'
      ];

      let widgetFound = false;
      
      for (const objName of possibleWidgetObjects) {
        if (windowAny[objName]) {
          console.log(`Found Waseel widget object: ${objName}`);
          
          const config = this.configService.getConfig('production');
          
          // Try different initialization methods
          if (typeof windowAny[objName].init === 'function') {
            windowAny[objName].init(config);
            console.log(`Waseel widget initialized via ${objName}.init()`);
            widgetFound = true;
            break;
          } else if (typeof windowAny[objName].start === 'function') {
            windowAny[objName].start(config);
            console.log(`Waseel widget started via ${objName}.start()`);
            widgetFound = true;
            break;
          } else if (typeof windowAny[objName].setup === 'function') {
            windowAny[objName].setup(config);
            console.log(`Waseel widget setup via ${objName}.setup()`);
            widgetFound = true;
            break;
          }
        }
      }

      if (!widgetFound) {
        // Check for any objects with 'waseel' in the name (case insensitive)
        const waseelObjects = Object.keys(window).filter(key => 
          key.toLowerCase().includes('waseel') || key.toLowerCase().includes('chat')
        );
        
        if (waseelObjects.length > 0) {
          console.log('Found potential Waseel objects:', waseelObjects);
          
          // Try to initialize with the first one that has an init method
          for (const objName of waseelObjects) {
            if (windowAny[objName] && typeof windowAny[objName].init === 'function') {
              try {
                windowAny[objName].init(this.configService.getConfig('production'));
                console.log(`Successfully initialized via ${objName}`);
                widgetFound = true;
                break;
              } catch (err) {
                console.log(`Failed to initialize via ${objName}:`, err);
              }
            }
          }
        }
        
        if (!widgetFound) {
          console.log('Widget script loaded but no recognizable widget API found.');
          console.log('This might be normal - the widget may initialize automatically or use a different approach.');
          
          // Check if any chat-like elements appeared in the DOM
          setTimeout(() => {
            const chatElements = document.querySelectorAll([
              '[class*="chat"]',
              '[class*="widget"]', 
              '[class*="waseel"]',
              'iframe[src*="chat"]',
              'iframe[src*="widget"]'
            ].join(','));
            
            if (chatElements.length > 0) {
              console.log('✅ Chat/widget elements found in DOM:', chatElements);
            } else {
              console.log('ℹ️ No obvious chat elements found, but widget may still be working');
            }
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error initializing Waseel widget:', error);
    }
  }

  // Check if widget script is loaded
  isLoaded(): boolean {
    return this.scriptLoaded;
  }

  /**
   * Apply custom colors to the widget dynamically
   * @param colors Object containing color values
   */
  applyCustomColors(colors: {
    primary: string;
    secondary: string;
    hover: string;
    text: string;
  }): void {
    console.log('🎨 Applying custom colors to Waseel widget:', colors);
    
    const applyColors = () => {
      // Find all potential widget elements
      const selectors = [
        '[class*="chat"] button',
        '[class*="widget"] button',
        '[id*="chat"] button',
        '[id*="widget"] button',
        '[data-waseel-widget] .widget-button',
        '.waseel-widget .widget-button',
        '#waseel-widget .widget-button'
      ];

      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: Element) => {
          const htmlElement = element as HTMLElement;
          
          // Apply button colors
          htmlElement.style.setProperty('background-color', colors.primary, 'important');
          htmlElement.style.setProperty('border-color', colors.secondary, 'important');
          htmlElement.style.setProperty('color', colors.text, 'important');
          htmlElement.style.setProperty('box-shadow', `0 4px 12px ${colors.primary}40`, 'important');
          
          // Add hover listeners
          htmlElement.addEventListener('mouseover', () => {
            htmlElement.style.setProperty('background-color', colors.hover, 'important');
            htmlElement.style.setProperty('border-color', colors.text, 'important');
            htmlElement.style.setProperty('transform', 'scale(1.05)', 'important');
          });
          
          htmlElement.addEventListener('mouseout', () => {
            htmlElement.style.setProperty('background-color', colors.primary, 'important');
            htmlElement.style.setProperty('border-color', colors.secondary, 'important');
            htmlElement.style.setProperty('transform', 'scale(1)', 'important');
          });
        });
      });

      // Apply colors to chat windows
      const windowSelectors = [
        '[class*="chat-window"]',
        '[class*="widget-window"]',
        '[class*="chat-header"]',
        '[class*="widget-header"]'
      ];

      windowSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: Element) => {
          const htmlElement = element as HTMLElement;
          
          if (selector.includes('header')) {
            htmlElement.style.setProperty('background', 
              `linear-gradient(135deg, ${colors.primary} 0%, ${colors.hover} 100%)`, 'important');
            htmlElement.style.setProperty('color', colors.text, 'important');
          } else {
            htmlElement.style.setProperty('background-color', colors.secondary, 'important');
            htmlElement.style.setProperty('border-color', colors.primary, 'important');
          }
        });
      });
    };

    // Apply colors with multiple attempts to catch dynamic content
    applyColors();
    setTimeout(applyColors, 1000);
    setTimeout(applyColors, 3000);
    setTimeout(applyColors, 5000);
  }
}